import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import {
  Button,
  ButtonSet,
  Checkbox,
  Form,
  FormGroup,
  Loading,
  MultiSelect,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import { LoadingState } from "underwriter_dashboard/shared/types";

import APIClient from "web_modules/APIClient";
import Uploader, { deleteUpload, restoreUpload } from "web_modules/shared/carbon/Uploader";
import { isUserAdmin } from "web_modules/shared/session";
import { formatPhoneNumber } from "@eave/shared/utils/phone";
import { EditingState } from "web_modules/shared/BaseTable";
import {
  ContentType,
  HLHLContactBranch,
  HLHLContactBranchDisplayValues,
  HLHLContactField,
  HLHLContactRole,
  HLHLContactRoleDisplayValues,
  HLHLContactSource,
  HLHLContactSourceDisplayValues,
  StateNames,
  States,
  Upload,
  UploadType,
} from "@eave/shared/types/api";
import { nullableStringAsEmpty, makeEvent } from "web_modules/shared/FormHelpers/carbon-helpers";
import { initialValues, validationSchema } from "./FormValues";
import { formatPhone } from ".";

const getContactRoles = async (): Promise<HLHLContactRole[]> => {
  return APIClient.get("/oa/hlhl_contacts/roles").then(({ roles }) => roles);
};

const revertUnsavedUploadChanges = (originalUploadId: number, currentUploadId: number) => {
  if (!!originalUploadId && !currentUploadId) {
    restoreUpload(originalUploadId);
  } else if (!originalUploadId && !!currentUploadId) {
    deleteUpload(currentUploadId);
  }
};

const stateItems = Object.keys(States).map(s => ({
  id: States[s], // e.g., "CA"
  text: `${StateNames[States[s]]} (${States[s]})`, // e.g., "California (CA)"
}));

interface SlackUser {
  id: string;
  active: boolean;
  name: string;
}

const getSlackUsers = (): Promise<SlackUser[]> => {
  return APIClient.get("/oa/slack/users").then((users: SlackUser[]) =>
    users.sort((a, b) => {
      // if they are both active or inactive sort by name
      if (a.active === b.active) {
        const aName = a.name.toUpperCase();
        const bName = b.name.toUpperCase();

        if (aName < bName) {
          return -1;
        }
        if (aName > bName) {
          return 1;
        }
      }

      // otherwise choose the one that is active
      if (a.active) {
        return -1;
      }
      if (b.active) {
        return 1;
      }
      return 0;
    }),
  );
};

interface Props {
  formState: any;
  onSubmit: any;
  editingMode: EditingState;
  onRequestClose: Function;
  loading: LoadingState;
}

const AddContactForm: React.FC<Props> = ({
  formState,
  onSubmit,
  editingMode,
  onRequestClose,
  loading,
}) => {
  const [slackUsers, setSlackUsers] = useState<SlackUser[]>([]);
  const [contactRoles, setContactRoles] = useState<HLHLContactRole[]>([]);

  useEffect(() => {
    if (isUserAdmin()) {
      getContactRoles().then(roles => setContactRoles(roles));
      getSlackUsers().then(users => setSlackUsers(users));
    }
  }, []);

  const submitForm = async (values, { setSubmitting, setStatus, resetForm, setErrors }) => {
    // before submit
    const finalFields = {};

    if (values.role === HLHLContactRole.LoanOfficer) {
      let errors = {};

      if (!values.signature_image_upload_id) {
        errors = { ...errors, signature_image_upload_id: "signature image is required" };
      }
      if (!values.headshot_image_upload_id) {
        errors = { ...errors, headshot_image_upload_id: "headshot image is required" };
      }

      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        return;
      }
    }

    // filter ""
    Object.keys(values).forEach(value => {
      if (values[value]) {
        if (value === HLHLContactField.Phone) {
          // given a 10-digit phone number we are formatting it as xxx-xxx-xxxx
          finalFields[value] = formatPhone(values[value]);
        } else {
          finalFields[value] = values[value];
        }
      }
    });

    try {
      // submit
      await onSubmit(finalFields);

      // after submit
      resetForm({});
      setStatus({ success: true });
    } catch (error) {
      // on error
      setStatus({ success: false });
      setSubmitting(false);
      setErrors({ submit: error.message });

      throw error;
    }
  };

  return (
    <>
      <Loading active={loading === LoadingState.ACTIVE} />
      <Formik
        enableReinitialize
        onSubmit={submitForm}
        initialValues={formState}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setValues,
          handleSubmit,
          resetForm,
          isSubmitting,
        }) => {
          const {
            first_name,
            middle_name,
            last_name,
            suffix,
            preferred_first_name,
            email,
            role,
            phone,
            branch,
            source,
            nmls_id,
            nmls_licensed_states,
            slack_id,
            default_slack_notify,
            signature_image_upload_id,
            signature_image_s3_key,
            headshot_image_upload_id,
            headshot_image_s3_key,
            personalized_signup_link_fragment,
          } = values;

          const licensedStates = nmls_licensed_states ? nmls_licensed_states.split(",") : null;

          return (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <FormGroup legendText="">
                <Select
                  id="role"
                  name="role"
                  value={nullableStringAsEmpty(role)}
                  invalid={!!errors.role && touched.role}
                  invalidText={errors.role}
                  labelText="Role"
                  onBlur={handleBlur}
                  onChange={e => {
                    revertUnsavedUploadChanges(
                      formState.signature_image_upload_id,
                      signature_image_upload_id,
                    );
                    revertUnsavedUploadChanges(
                      formState.headshot_image_upload_id,
                      headshot_image_upload_id,
                    );
                    // when user changes `role` dropdown, keep only basic info from the last form
                    // which is unlikely to change; otherwise, reset fields to initial form values
                    // (which in particular will clear out any fields that are role-centric)
                    setValues({
                      ...initialValues,
                      id: values.id,
                      first_name: values.first_name,
                      middle_name: values.middle_name,
                      last_name: values.last_name,
                      suffix: values.suffix,
                      email: values.email,
                      active: values.active,
                    });
                    handleChange(e);
                  }}
                  size="sm"
                >
                  <SelectItem key="no-role" text="Select a role" value="" />
                  {Object.values(contactRoles).map(b => (
                    <SelectItem key={b} text={HLHLContactRoleDisplayValues[b]} value={b} />
                  ))}
                </Select>
                <div style={{ marginBottom: "10px" }} />
                <TextInput
                  id="email"
                  name="email"
                  size="sm"
                  value={email}
                  labelText="Email"
                  placeholder="employee@homelight.com"
                  type="text"
                  invalid={!!errors.email && touched.email}
                  invalidText={errors.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormGroup>
              <FormGroup legendText="">
                {role === HLHLContactRole.LoanOfficer && (
                  <>
                    <div style={{ marginBottom: "10px" }} />
                    <TextInput
                      id="preferred-first-name"
                      name="preferred_first_name"
                      size="sm"
                      value={preferred_first_name}
                      labelText="Preferred first name"
                      placeholder=""
                      type="text"
                      invalid={!!errors.preferred_first_name && touched.preferred_first_name}
                      invalidText={errors.preferred_first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </>
                )}
                <div style={{ marginBottom: "10px" }} />
                <TextInput
                  id="first-name"
                  name="first_name"
                  size="sm"
                  value={first_name}
                  labelText="First name"
                  placeholder=""
                  type="text"
                  invalid={!!errors.first_name && touched.first_name}
                  invalidText={errors.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div style={{ marginBottom: "10px" }} />
                <TextInput
                  id="middle-name"
                  name="middle_name"
                  size="sm"
                  value={nullableStringAsEmpty(middle_name)}
                  labelText="Middle name"
                  placeholder=""
                  type="text"
                  invalid={!!errors.middle_name && touched.middle_name}
                  invalidText={errors.middle_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div style={{ marginBottom: "10px" }} />
                <TextInput
                  id="last-name"
                  name="last_name"
                  size="sm"
                  value={last_name}
                  labelText="Last name"
                  placeholder=""
                  type="text"
                  invalid={!!errors.last_name && touched.last_name}
                  invalidText={errors.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div style={{ marginBottom: "10px" }} />
                <Select
                  id="suffix"
                  name="suffix"
                  value={nullableStringAsEmpty(suffix)}
                  invalid={!!errors.suffix && touched.suffix}
                  invalidText={errors.suffix}
                  labelText="Suffix"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  size="sm"
                >
                  <SelectItem text="Select a suffix" value="" />
                  <SelectItem text="Sr." value="Sr." />
                  <SelectItem text="Jr." value="Jr." />
                  <SelectItem text="II" value="II" />
                  <SelectItem text="III" value="III" />
                  <SelectItem text="IV" value="IV" />
                  <SelectItem text="V" value="V" />
                  <SelectItem text="VI" value="VI" />
                </Select>
                <div style={{ marginBottom: "10px" }} />
              </FormGroup>
              <FormGroup legendText="">
                {role === HLHLContactRole.LoanOfficer && (
                  <>
                    <TextInput
                      id="personalized_signup_link_fragment"
                      name="personalized_signup_link_fragment"
                      size="sm"
                      value={personalized_signup_link_fragment}
                      labelText={`Personalized link fragment: ${window.eaveEnv.apps.borrower}/lo/${personalized_signup_link_fragment}`}
                      placeholder="must contain only uppercase or lowercase letters"
                      type="text"
                      invalid={
                        !!errors.personalized_signup_link_fragment &&
                        touched.personalized_signup_link_fragment
                      }
                      invalidText={errors.personalized_signup_link_fragment}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <div style={{ marginBottom: "10px" }} />
                    <TextInput
                      id="nmls-id"
                      name="nmls_id"
                      size="sm"
                      value={nmls_id}
                      labelText="NMLS ID"
                      placeholder=""
                      type="text"
                      invalid={!!errors.nmls_id && touched.nmls_id}
                      invalidText={errors.nmls_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <div style={{ marginBottom: "10px" }} />
                    <MultiSelect.Filterable
                      id="nmls-licensed-states"
                      name="nmls_licensed_states"
                      size="sm"
                      type="text"
                      placeholder={
                        nmls_licensed_states
                          ? nmls_licensed_states.split(",").join(", ")
                          : "Select state(s)"
                      }
                      titleText="NMLS licensed states"
                      items={stateItems}
                      itemToString={item => (item ? item.text : "")}
                      initialSelectedItems={
                        licensedStates ? stateItems.filter(s => licensedStates.includes(s.id)) : []
                      }
                      onChange={e => {
                        const value = e.selectedItems
                          .map(i => i.id)
                          .sort()
                          .join(","); // e.g., "CA,CO,OH,TX"
                        handleChange(makeEvent("nmls_licensed_states", value));
                      }}
                      onBlur={handleBlur}
                    />
                    <Uploader
                      id="signature_uploader"
                      uploadType={UploadType.LoanOfficerSignatureImage}
                      contentTypes={[ContentType.Jpeg, ContentType.Png]}
                      details="Only .jpg and .png files are supported"
                      existingUpload={{
                        id: signature_image_upload_id,
                        s3_key: signature_image_s3_key,
                      }}
                      onUploadHook={(u: Upload) => {
                        handleChange(makeEvent("signature_image_upload_id", u.id));
                        handleChange(makeEvent("signature_image_s3_key", u.s3_key));
                      }}
                      onDeleteHook={() => {
                        handleChange(makeEvent("signature_image_upload_id", null));
                        handleChange(makeEvent("signature_image_s3_key", null));
                      }}
                      invalidText={errors.signature_image_upload_id}
                    />
                    <Uploader
                      id="headshot_uploader"
                      uploadType={UploadType.LoanOfficerHeadshotImage}
                      contentTypes={[ContentType.Jpeg, ContentType.Png]}
                      details="Only .jpg and .png files are supported"
                      existingUpload={{
                        id: headshot_image_upload_id,
                        s3_key: headshot_image_s3_key,
                      }}
                      onUploadHook={(u: Upload) => {
                        handleChange(makeEvent("headshot_image_upload_id", u.id));
                        handleChange(makeEvent("headshot_image_s3_key", u.s3_key));
                      }}
                      onDeleteHook={() => {
                        handleChange(makeEvent("headshot_image_upload_id", null));
                        handleChange(makeEvent("headshot_image_s3_key", null));
                      }}
                      invalidText={errors.headshot_image_upload_id}
                    />
                    <div style={{ marginBottom: "10px" }} />
                  </>
                )}
                {role !== HLHLContactRole.SettlementAgent &&
                  role !== HLHLContactRole.QualityControl &&
                  role !== HLHLContactRole.CashClose &&
                  role !== HLHLContactRole.CashCloseAdmin && (
                    <>
                      <Select
                        id="slack-id"
                        name="slack_id"
                        value={nullableStringAsEmpty(slack_id)}
                        invalid={!!errors.slack_id && touched.slack_id}
                        invalidText="Slack ID required if 'Add to every new loan channel' selected"
                        labelText="Slack user"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size="sm"
                      >
                        <SelectItem key="no-user" text="Select a user" value="" />
                        {slackUsers.map(b => (
                          <SelectItem
                            key={b.id}
                            text={b.name + (b.active ? "" : " - (inactive)")}
                            value={b.id}
                          />
                        ))}
                      </Select>
                      <div style={{ marginBottom: "10px" }} />
                      <Checkbox
                        id="default_slack_notify"
                        name="default_slack_notify"
                        checked={default_slack_notify}
                        labelText="Add to every new loan channel"
                        onBlur={handleBlur}
                        onChange={() => {
                          handleChange(makeEvent("default_slack_notify", !default_slack_notify));
                        }}
                      />
                      <div style={{ marginBottom: "10px" }} />
                    </>
                  )}
                {role !== HLHLContactRole.SettlementAgent &&
                  role !== HLHLContactRole.LoanOfficerAssistant &&
                  role !== HLHLContactRole.QualityControl &&
                  role !== HLHLContactRole.CashClose &&
                  role !== HLHLContactRole.CashCloseAdmin && (
                    <>
                      <TextInput
                        id="phone"
                        name="phone"
                        size="sm"
                        value={phone && formatPhoneNumber(phone)}
                        labelText="Phone Number"
                        placeholder=""
                        type="text"
                        invalid={!!errors.phone && touched.phone}
                        invalidText="Enter a valid phone number"
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <div style={{ marginBottom: "10px" }} />
                      <Select
                        id="branch"
                        name="branch"
                        value={nullableStringAsEmpty(branch)}
                        invalid={!!errors.branch && touched.branch}
                        invalidText={errors.branch}
                        labelText="Branch"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size="sm"
                      >
                        <SelectItem key="no-branch" text="Select a branch" value="" />
                        {Object.values(HLHLContactBranch).map(b => (
                          <SelectItem key={b} text={HLHLContactBranchDisplayValues[b]} value={b} />
                        ))}
                      </Select>
                      <div style={{ marginBottom: "10px" }} />
                      <Select
                        id="source"
                        name="source"
                        value={nullableStringAsEmpty(source)}
                        invalid={!!errors.source && touched.source}
                        invalidText={errors.source}
                        labelText="Source"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        size="sm"
                      >
                        <SelectItem key="no-source" text="Select a source" value="" />
                        {Object.values(HLHLContactSource).map(b => (
                          <SelectItem key={b} text={HLHLContactSourceDisplayValues[b]} value={b} />
                        ))}
                      </Select>
                      <div style={{ marginBottom: "10px" }} />
                    </>
                  )}
              </FormGroup>
              <ButtonSet>
                <Button
                  kind="secondary"
                  size="lg"
                  onClick={() => {
                    revertUnsavedUploadChanges(
                      formState.signature_image_upload_id,
                      signature_image_upload_id,
                    );
                    revertUnsavedUploadChanges(
                      formState.headshot_image_upload_id,
                      headshot_image_upload_id,
                    );
                    resetForm();
                    onRequestClose();
                  }}
                  style={{ maxWidth: "50%" }}
                  disable={isSubmitting ? "true" : "false"}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  size="lg"
                  type="submit"
                  style={{ maxWidth: "50%" }}
                  disable={isSubmitting ? "true" : "false"}
                >
                  {editingMode === EditingState.CREATE ? "Save" : "Update"}
                </Button>
              </ButtonSet>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AddContactForm;
